import { FC, useMemo } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import {
  EXERCISE_METHOD,
  EXERCISE_METHOD_LABELS,
  LOAD_UNIT,
  LOAD_UNIT_LABELS,
  getLoadUnitsForInputMode,
} from '../../../enums/programming';
import styles from './WorkoutBuilderModal.module.scss';

// --- Types ---

/** Shape of the method-specific params managed by the adapter */
export interface MethodParams {
  /** Standard / shared */
  sets?: string;
  reps?: string;
  loadValue?: string;
  loadUnit?: LOAD_UNIT;
  rest?: string;
  /** EMOM — map to methodConfig.totalDurationMin / intervalSec */
  totalDurationMin?: string;  // durata totale (valore grezzo, interpretato in base a durationUnit)
  durationUnit?: 'min' | 'sec'; // default 'min'
  intervalSec?: string;       // ogni X (valore grezzo, interpretato in base a intervalUnit)
  intervalUnit?: 'min' | 'sec'; // default 'sec'
  emomReps?: string;          // reps per round
  emomTempo?: string;         // tempo di esecuzione pesistico (es. 3010)
  /** Tabata */
  onSec?: string;             // ON (sec)
  offSec?: string;            // OFF (sec)
  rounds?: string;            // round
  tabataSets?: string;        // set — kept unique to avoid collision with standard `sets`; mapped to methodConfig.sets in buildPayload
  restBetweenSetsSec?: string; // rec tra set
  /** Tempo — 4 fasi */
  eccentricSec?: string;      // eccentrica
  pauseBottomSec?: string;    // pausa bassa
  concentricSec?: string;     // concentrica (o "X" esplosivo)
  pauseTopSec?: string;       // pausa alta
  isExplosive?: string;       // "true" | "false"
  /** Isometrico */
  durationSec?: string;       // durata hold (sec)
  angleDeg?: string;          // angolo (°), opzionale
  /** Cluster */
  repsPerCluster?: string;    // reps per mini-set
  clustersPerSet?: string;    // numero mini-set
  intraClusterRestSec?: string; // pausa intra-cluster (sec)
  /** Rest-Pause */
  initialReps?: string;       // reps set iniziale
  intraRestSec?: string;      // pausa (sec)
  rpDropReps?: string;        // reps per drop (mapped to drops[] in buildPayload)
  rpDrops?: string;           // numero drop   (mapped to drops[] in buildPayload)
  /** AMRAP */
  timeLimitSec?: string;      // durata (sec)
}

export interface MethodFieldsAdapterProps {
  method: EXERCISE_METHOD | '';
  params: MethodParams;
  /** Input mode dell'esercizio: filtra le unità ammesse nel selettore (BUG E). */
  inputMode?: string;
  onChange: (patch: Partial<MethodParams>) => void;
}

// --- Field config per method ---

interface FieldDef {
  key: keyof MethodParams;
  label: string;
  placeholder: string;
  type?: 'text' | 'number' | 'toggle';
  /** If true, render the load unit selector next to this field */
  withUnit?: boolean;
  /** If true, render a min/sec time-unit selector next to this field */
  withTimeUnit?: boolean;
  /** Key in MethodParams for the time unit value */
  timeUnitKey?: keyof MethodParams;
  /** Width hint: 'sm' (60px), 'md' (90px), 'lg' (120px) */
  width?: 'sm' | 'md' | 'lg';
  /** Tooltip shown on the field label */
  tooltip?: string;
}

const STANDARD_FIELDS: FieldDef[] = [
  { key: 'sets', label: 'Serie', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'reps', label: 'Reps', placeholder: '8', width: 'sm' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero', placeholder: '90"', width: 'md' },
];

const EMOM_FIELDS: FieldDef[] = [
  { key: 'totalDurationMin', label: 'Durata', placeholder: '10', type: 'number', width: 'md', withTimeUnit: true, timeUnitKey: 'durationUnit' },
  { key: 'intervalSec', label: 'Ogni', placeholder: '60', type: 'number', width: 'md', withTimeUnit: true, timeUnitKey: 'intervalUnit' },
  { key: 'emomReps', label: 'Reps/round', placeholder: '8', width: 'sm' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  {
    key: 'emomTempo',
    label: 'Tempo',
    placeholder: '3010',
    width: 'sm',
    tooltip: 'Tempo di esecuzione (es. 3010 = 3s eccentrica · 0 pausa bassa · 1s concentrica · 0 pausa alta)',
  },
];

const TABATA_FIELDS: FieldDef[] = [
  { key: 'onSec', label: 'ON (sec)', placeholder: '20', type: 'number', width: 'sm' },
  { key: 'offSec', label: 'OFF (sec)', placeholder: '10', type: 'number', width: 'sm' },
  { key: 'rounds', label: 'Round', placeholder: '8', type: 'number', width: 'sm' },
  { key: 'tabataSets', label: 'Set', placeholder: '1', type: 'number', width: 'sm' },
  { key: 'restBetweenSetsSec', label: 'Rec tra set', placeholder: '60"', width: 'md' },
];

const TEMPO_FIELDS: FieldDef[] = [
  { key: 'sets', label: 'Serie', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'reps', label: 'Reps', placeholder: '8', width: 'sm' },
  { key: 'eccentricSec', label: 'Ecc', placeholder: '3', width: 'sm' },
  { key: 'pauseBottomSec', label: 'Pausa bassa', placeholder: '1', width: 'sm' },
  { key: 'concentricSec', label: 'Conc', placeholder: 'X', width: 'sm' },
  { key: 'isExplosive', label: 'Esplosivo', placeholder: '', type: 'toggle', width: 'sm' },
  { key: 'pauseTopSec', label: 'Pausa alta', placeholder: '0', width: 'sm' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero', placeholder: '90"', width: 'md' },
];

const ISOMETRIC_FIELDS: FieldDef[] = [
  { key: 'sets', label: 'Serie', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'durationSec', label: 'Durata hold (sec)', placeholder: '30', type: 'number', width: 'md' },
  { key: 'angleDeg', label: 'Angolo (°)', placeholder: '90', type: 'number', width: 'sm' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero', placeholder: '90"', width: 'md' },
];

const CLUSTER_FIELDS: FieldDef[] = [
  { key: 'sets', label: 'Serie', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'repsPerCluster', label: 'Reps/mini-set', placeholder: '2', type: 'number', width: 'sm' },
  { key: 'clustersPerSet', label: 'Mini-set', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'intraClusterRestSec', label: 'Pausa intra (sec)', placeholder: '15', type: 'number', width: 'md' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero lungo', placeholder: '180"', width: 'md' },
];

const REST_PAUSE_FIELDS: FieldDef[] = [
  { key: 'sets', label: 'Serie', placeholder: '1', type: 'number', width: 'sm' },
  { key: 'initialReps', label: 'Reps iniziali', placeholder: '8', type: 'number', width: 'sm' },
  { key: 'intraRestSec', label: 'Pausa (sec)', placeholder: '15', type: 'number', width: 'sm' },
  { key: 'rpDropReps', label: 'Reps/drop', placeholder: '3', type: 'number', width: 'sm' },
  { key: 'rpDrops', label: 'N. drop', placeholder: '2', type: 'number', width: 'sm' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero', placeholder: '120"', width: 'md' },
];

const AMRAP_FIELDS: FieldDef[] = [
  { key: 'timeLimitSec', label: 'Durata (sec)', placeholder: '600', type: 'number', width: 'md' },
  { key: 'loadValue', label: 'Carico', placeholder: '—', withUnit: true, width: 'md' },
  { key: 'rest', label: 'Recupero', placeholder: '—', width: 'md' },
];

const FIELDS_BY_METHOD: Record<EXERCISE_METHOD, FieldDef[]> = {
  [EXERCISE_METHOD.STANDARD]: STANDARD_FIELDS,
  [EXERCISE_METHOD.EMOM]: EMOM_FIELDS,
  [EXERCISE_METHOD.TABATA]: TABATA_FIELDS,
  [EXERCISE_METHOD.TEMPO]: TEMPO_FIELDS,
  [EXERCISE_METHOD.ISOMETRIC]: ISOMETRIC_FIELDS,
  [EXERCISE_METHOD.CLUSTER]: CLUSTER_FIELDS,
  [EXERCISE_METHOD.REST_PAUSE]: REST_PAUSE_FIELDS,
  [EXERCISE_METHOD.AMRAP]: AMRAP_FIELDS,
  [EXERCISE_METHOD.DROPSET]: STANDARD_FIELDS,
  [EXERCISE_METHOD.PYRAMID]: STANDARD_FIELDS,
};

// --- Width helpers ---

const WIDTH_MAP: Record<string, string> = {
  sm: '60px',
  md: '90px',
  lg: '120px',
};

// --- Unit selector sub-components ---

/** Load unit selector with "Più usate" group at top */
const PREFERRED_UNITS = [LOAD_UNIT.KG, LOAD_UNIT.LB, LOAD_UNIT.PERCENT_1RM, LOAD_UNIT.PERCENT_BW];

interface UnitSelectorProps {
  value: LOAD_UNIT;
  onChange: (unit: LOAD_UNIT) => void;
  label?: string;
  /** Input mode dell'esercizio: filtra le unità ammesse (BUG E). */
  inputMode?: string;
}

const UnitSelector: FC<UnitSelectorProps> = ({ value, onChange, label, inputMode }) => {
  const allowedUnits = getLoadUnitsForInputMode(inputMode);
  const POPULAR_UNITS = PREFERRED_UNITS.filter((u) => allowedUnits.includes(u));
  const OTHER_UNITS = allowedUnits.filter((u) => !PREFERRED_UNITS.includes(u));
  return (
  <div className={styles.fieldGroup} style={{ minWidth: 80 }}>
    {label && <label className={styles.label}>{label}</label>}
    <div className={styles.selectWrapper}>
      <select
        className={styles.unitSelect}
        value={value}
        onChange={(e) => onChange(e.target.value as LOAD_UNIT)}
      >
        <optgroup label="Più usate">
          {POPULAR_UNITS.map((u) => (
            <option key={u} value={u}>
              {LOAD_UNIT_LABELS[u]}
            </option>
          ))}
        </optgroup>
        <optgroup label="Altre">
          {OTHER_UNITS.map((u) => (
            <option key={u} value={u}>
              {LOAD_UNIT_LABELS[u]}
            </option>
          ))}
        </optgroup>
      </select>
      <CaretDown size={12} className={styles.selectIcon} />
    </div>
  </div>
  );
};

/** Min/sec dropdown for duration and interval fields */
interface TimeUnitSelectorProps {
  value: 'min' | 'sec';
  onChange: (unit: 'min' | 'sec') => void;
}

const TimeUnitSelector: FC<TimeUnitSelectorProps> = ({ value, onChange }) => (
  <div className={styles.fieldGroup} style={{ minWidth: 58 }}>
    <label className={styles.label}>&nbsp;</label>
    <div className={styles.selectWrapper}>
      <select
        className={styles.unitSelect}
        value={value}
        onChange={(e) => onChange(e.target.value as 'min' | 'sec')}
      >
        <option value="min">min</option>
        <option value="sec">sec</option>
      </select>
      <CaretDown size={12} className={styles.selectIcon} />
    </div>
  </div>
);

// --- Dynamic label / placeholder by method + unit ---

function getLoadLabel(method: EXERCISE_METHOD | '', unit: LOAD_UNIT): string {
  switch (unit) {
    case LOAD_UNIT.RPE:
      return 'RPE';
    case LOAD_UNIT.RIR:
      return 'RIR';
    case LOAD_UNIT.PERCENT_1RM:
      return '% 1RM';
    case LOAD_UNIT.PERCENT_BW:
      return '% BW';
    case LOAD_UNIT.SEC:
      return 'Durata (sec)';
    case LOAD_UNIT.MS:
      return 'Velocita (m/s)';
    case LOAD_UNIT.DEGREE:
      return 'Angolo (°)';
    default:
      break;
  }

  if (method === EXERCISE_METHOD.ISOMETRIC) return 'Carico hold';
  return 'Carico';
}

function getLoadPlaceholder(unit: LOAD_UNIT): string {
  switch (unit) {
    case LOAD_UNIT.KG:
    case LOAD_UNIT.LB:
      return 'es. 80';
    case LOAD_UNIT.PERCENT_1RM:
      return 'es. 75';
    case LOAD_UNIT.PERCENT_BW:
      return 'es. 100';
    case LOAD_UNIT.RPE:
      return '1-10';
    case LOAD_UNIT.RIR:
      return '0-5';
    case LOAD_UNIT.MS:
      return 'es. 0.8';
    case LOAD_UNIT.SEC:
      return 'es. 30';
    case LOAD_UNIT.DEGREE:
      return 'es. 90';
    default:
      return '—';
  }
}

function getUnitLabel(method: EXERCISE_METHOD | ''): string {
  if (method === EXERCISE_METHOD.ISOMETRIC) return 'Unita hold';
  return 'Unita';
}

// --- EMOM rounds calculation ---

interface EmomRoundsInfo {
  rounds: number;
  isIntervalTooLong: boolean;
  isNotInteger: boolean;
  lastRoundSec: number;
}

function calcEmomRounds(params: MethodParams): EmomRoundsInfo | null {
  const durationVal = parseFloat(params.totalDurationMin ?? '');
  const intervalVal = parseFloat(params.intervalSec ?? '');
  if (isNaN(durationVal) || isNaN(intervalVal) || durationVal <= 0 || intervalVal <= 0) return null;

  const durationUnit = params.durationUnit ?? 'min';
  const intervalUnit = params.intervalUnit ?? 'sec';
  const durationInSec = durationUnit === 'min' ? durationVal * 60 : durationVal;
  const intervalInSec = intervalUnit === 'min' ? intervalVal * 60 : intervalVal;

  if (intervalInSec > durationInSec) {
    return { rounds: 0, isIntervalTooLong: true, isNotInteger: false, lastRoundSec: 0 };
  }

  const rawRounds = durationInSec / intervalInSec;
  const roundsFloor = Math.floor(rawRounds);
  const isNotInteger = Math.abs(rawRounds - roundsFloor) > 0.001;
  const lastRoundSec = isNotInteger ? durationInSec - roundsFloor * intervalInSec : 0;

  return { rounds: roundsFloor, isIntervalTooLong: false, isNotInteger, lastRoundSec };
}

// --- Main component ---

const MethodFieldsAdapter: FC<MethodFieldsAdapterProps> = ({
  method,
  params,
  inputMode,
  onChange,
}) => {
  const effectiveMethod = method || EXERCISE_METHOD.STANDARD;
  const fields = FIELDS_BY_METHOD[effectiveMethod as EXERCISE_METHOD] ?? STANDARD_FIELDS;

  const currentUnit = params.loadUnit ?? LOAD_UNIT.KG;

  const dynamicLoadLabel = useMemo(() => getLoadLabel(method, currentUnit), [method, currentUnit]);
  const dynamicLoadPlaceholder = useMemo(() => getLoadPlaceholder(currentUnit), [currentUnit]);
  const dynamicUnitLabel = useMemo(() => getUnitLabel(method), [method]);

  const emomRoundsInfo = useMemo(
    () => effectiveMethod === EXERCISE_METHOD.EMOM ? calcEmomRounds(params) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveMethod, params.totalDurationMin, params.intervalSec, params.durationUnit, params.intervalUnit],
  );

  const methodLabel = effectiveMethod !== EXERCISE_METHOD.STANDARD
    ? EXERCISE_METHOD_LABELS[effectiveMethod as EXERCISE_METHOD]
    : null;

  return (
    <div className={styles.methodFieldsAdapter}>
      {/* Method badge */}
      {methodLabel && (
        <span className={styles.methodBadge}>{methodLabel}</span>
      )}

      <div className={styles.methodFieldsRow}>
        {fields.map((field) => {
          // Load field with unit selector
          if (field.withUnit) {
            return (
              <div key={field.key} className={styles.methodFieldPair}>
                <div
                  className={styles.fieldGroup}
                  style={{ minWidth: WIDTH_MAP[field.width ?? 'md'] }}
                >
                  <label className={styles.label}>{dynamicLoadLabel}</label>
                  <input
                    type={field.type ?? 'text'}
                    className={styles.smallInput}
                    placeholder={dynamicLoadPlaceholder}
                    value={(params[field.key] as string) ?? ''}
                    onChange={(e) => onChange({ [field.key]: e.target.value })}
                  />
                </div>
                <UnitSelector
                  label={dynamicUnitLabel}
                  value={currentUnit}
                  inputMode={inputMode}
                  onChange={(unit) => onChange({ loadUnit: unit })}
                />
              </div>
            );
          }

          // Duration/interval field with min/sec unit selector
          if (field.withTimeUnit && field.timeUnitKey) {
            const timeUnitKey = field.timeUnitKey;
            const currentTimeUnit =
              (params[timeUnitKey] as 'min' | 'sec') ??
              (timeUnitKey === 'durationUnit' ? 'min' : 'sec');
            return (
              <div key={field.key} className={styles.methodFieldPair}>
                <div
                  className={styles.fieldGroup}
                  style={{ minWidth: WIDTH_MAP[field.width ?? 'md'] }}
                >
                  <label className={styles.label}>{field.label}</label>
                  <input
                    type="number"
                    className={styles.smallInput}
                    placeholder={field.placeholder}
                    value={(params[field.key] as string) ?? ''}
                    onChange={(e) => onChange({ [field.key]: e.target.value })}
                  />
                </div>
                <TimeUnitSelector
                  value={currentTimeUnit}
                  onChange={(u) => onChange({ [timeUnitKey]: u })}
                />
              </div>
            );
          }

          // Toggle field (e.g. isExplosive)
          if (field.type === 'toggle') {
            const isActive = (params[field.key] as string) === 'true';
            return (
              <div
                key={field.key}
                className={styles.fieldGroup}
                style={{ minWidth: WIDTH_MAP[field.width ?? 'sm'] }}
              >
                <label className={styles.label}>{field.label}</label>
                <button
                  type="button"
                  className={styles.smallInput}
                  style={{
                    cursor: 'pointer',
                    background: isActive ? '#3182ce' : 'transparent',
                    color: isActive ? '#fff' : 'inherit',
                    border: isActive ? '1px solid #3182ce' : undefined,
                    fontWeight: isActive ? 700 : 400,
                    textAlign: 'center',
                    padding: '0 4px',
                  }}
                  onClick={() => onChange({ [field.key]: isActive ? 'false' : 'true' })}
                >
                  {isActive ? 'SI' : 'NO'}
                </button>
              </div>
            );
          }

          return (
            <div
              key={field.key}
              className={styles.fieldGroup}
              style={{ minWidth: WIDTH_MAP[field.width ?? 'md'] }}
            >
              <label
                className={styles.label}
                title={field.tooltip}
                style={field.tooltip ? { cursor: 'help', borderBottom: '1px dotted #b5b5b5' } : undefined}
              >
                {field.label}
              </label>
              <input
                type={field.type ?? 'text'}
                className={styles.smallInput}
                placeholder={field.placeholder}
                value={(params[field.key] as string) ?? ''}
                onChange={(e) => onChange({ [field.key]: e.target.value })}
              />
            </div>
          );
        })}
      </div>

      {/* EMOM: rounds badge + validation */}
      {effectiveMethod === EXERCISE_METHOD.EMOM && emomRoundsInfo && (
        <div className={styles.emomRoundsBadgeRow}>
          {emomRoundsInfo.isIntervalTooLong ? (
            <span className={styles.emomRoundsError}>
              ⚠ L'intervallo supera la durata totale
            </span>
          ) : (
            <>
              <span className={styles.emomRoundsBadge}>
                {emomRoundsInfo.rounds} round
              </span>
              {emomRoundsInfo.isNotInteger && (
                <span className={styles.emomRoundsWarning}>
                  ⚠ Ultimo round: {Math.round(emomRoundsInfo.lastRoundSec)}s
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MethodFieldsAdapter;
